/**
 * Gift with purchase (GWP) — pure vanilla JS.
 *
 * Drives the side-cart free-gift banner ([data-cart-gwp-banner]):
 *   - Shows the first applicable config when the cart qualifies
 *   - Hides when the gift is already in the cart
 *   - Adds the gift via /cart/add.js (no page navigation)
 *   - Re-checks qualification on mini-cart and cart page changes
 */
(function () {
  "use strict";

  var GWP_MAX_LINE_QUANTITY = 1;
  var SYNC_DEBOUNCE_MS = 120;
  var BANNER_SELECTOR = "[data-cart-gwp-banner]";

  function getGwpConfigs() {
    var raw = window.gwp_configs;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.filter(function (cfg) {
      return cfg && cfg.variant_id != null && cfg.variant_id !== "";
    });
  }

  function gwpConfigKey(cfg) {
    var key = cfg.id != null ? cfg.id : cfg.handle != null ? cfg.handle : cfg.variant_id;
    return String(key == null ? "" : key);
  }

  function isFreeProductProperty(value) {
    return value === 1 || value === "1" || value === true;
  }

  function collectionProductIdSet(cfg) {
    var ids = cfg.collection_product_ids || [];
    var set = {};
    ids.forEach(function (id) {
      set[String(id)] = true;
    });
    return set;
  }

  function parseTrigger(cfg) {
    var trigger = parseInt(String(cfg.quantity_trigger == null ? "1" : cfg.quantity_trigger), 10);
    if (!isFinite(trigger) || trigger < 1) {
      return 1;
    }
    return trigger;
  }

  function giftLinesForConfig(cartItems, cfg) {
    var giftVid = String(cfg.variant_id);
    var configKey = gwpConfigKey(cfg);

    return cartItems.filter(function (item) {
      if (!item || String(item.variant_id) !== giftVid) {
        return false;
      }

      var props = item.properties || {};
      if (!isFreeProductProperty(props["_free-product"])) {
        return false;
      }

      var lineConfigId = props["_gwp_config_id"];
      if (lineConfigId == null || lineConfigId === "") {
        return true;
      }

      return String(lineConfigId) === configKey;
    });
  }

  function qualifyingQuantity(cartItems, giftVariantId, collectionIds) {
    return cartItems.reduce(function (sum, item) {
      if (!item || String(item.variant_id) === giftVariantId) {
        return sum;
      }
      if (!collectionIds[String(item.product_id)]) {
        return sum;
      }
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (res) {
      return res.json();
    });
  }

  function isErrorResponse(res) {
    return !!(res && (res.errors || res.status === 422 || res.message));
  }

  function changeLineQuantity(lineKey, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    }).then(function (res) {
      return res.json();
    });
  }

  function addGift(giftVid, configKey) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: Number(giftVid) || giftVid,
            quantity: GWP_MAX_LINE_QUANTITY,
            properties: {
              "_free-product": "1",
              "_gwp_config_id": configKey,
            },
          },
        ],
      }),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          return {
            errors: data.description || data.message || "Could not add free gift",
            status: res.status,
          };
        }
        return data;
      });
    });
  }

  function removeGiftLines(lines) {
    var failed = false;
    var lastCart = null;
    var chain = Promise.resolve();

    lines.forEach(function (line) {
      if (!line || !line.key) {
        return;
      }
      chain = chain.then(function () {
        if (failed) {
          return null;
        }
        return changeLineQuantity(line.key, 0).then(function (res) {
          if (isErrorResponse(res)) {
            failed = true;
            return null;
          }
          lastCart = res;
          return res;
        });
      });
    });

    return chain.then(function () {
      return failed ? null : lastCart;
    });
  }

  function giftVariantInCart(cartItems, cfg) {
    var giftVid = String(cfg.variant_id);
    return cartItems.some(function (item) {
      return item && String(item.variant_id) === giftVid;
    });
  }

  function selectApplicableConfig(cartItems) {
    var configs = getGwpConfigs();

    for (var i = 0; i < configs.length; i++) {
      var cfg = configs[i];
      var collectionIds = collectionProductIdSet(cfg);

      if (Object.keys(collectionIds).length === 0) {
        continue;
      }

      if (giftVariantInCart(cartItems, cfg)) {
        continue;
      }

      var qualifying = qualifyingQuantity(cartItems, String(cfg.variant_id), collectionIds);
      if (qualifying >= parseTrigger(cfg)) {
        return cfg;
      }
    }

    return null;
  }

  function removeStaleGifts(cartItems) {
    var configs = getGwpConfigs();
    var staleLines = [];

    configs.forEach(function (cfg) {
      var collectionIds = collectionProductIdSet(cfg);
      var giftLines = giftLinesForConfig(cartItems, cfg);

      if (giftLines.length === 0) {
        return;
      }

      var qualifying = qualifyingQuantity(cartItems, String(cfg.variant_id), collectionIds);
      if (qualifying < parseTrigger(cfg)) {
        staleLines = staleLines.concat(giftLines);
      }
    });

    if (staleLines.length === 0) {
      return Promise.resolve(false);
    }

    return removeGiftLines(staleLines).then(function (data) {
      return data != null;
    });
  }

  function hideBanner(banner) {
    banner.hidden = true;
    banner.setAttribute("data-gwp-config-id", "");
    banner.setAttribute("data-gwp-variant-id", "");
  }

  function renderBanner(banner, cfg) {
    banner.setAttribute("data-gwp-config-id", gwpConfigKey(cfg));
    banner.setAttribute("data-gwp-variant-id", String(cfg.variant_id));

    var img = banner.querySelector(".free-product-gwp__image img");
    if (img) {
      if (cfg.image) {
        img.src = cfg.image;
        img.alt = cfg.title || "Free Gift!";
        img.style.display = "";
      } else {
        img.removeAttribute("src");
        img.style.display = "none";
      }
    }

    var title = banner.querySelector(".free-product-gwp__title");
    if (title) {
      title.textContent = cfg.title || "";
    }

    var description = banner.querySelector(".free-product-gwp__description");
    if (description) {
      description.innerHTML = cfg.description || "";
      description.style.display = cfg.description ? "" : "none";
    }

    banner.hidden = false;
  }

  function updateBanner(cartItems) {
    var banner = document.querySelector(BANNER_SELECTOR);
    if (!banner) {
      return;
    }

    var cfg = selectApplicableConfig(cartItems);
    if (!cfg) {
      hideBanner(banner);
      return;
    }

    renderBanner(banner, cfg);
  }

  /** Re-fetch cart HTML and update mini-cart items + footer (no navigation). */
  function refreshSideCartContents() {
    var inner = document.querySelector(".side-cart__inner");
    var footer = document.querySelector(".side-cart__footer");

    return fetch("/cart")
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");
        var items = doc.querySelector(".main-cart-items-table");

        if (items && inner) {
          inner.innerHTML = items.outerHTML;
          inner.classList.remove("side-cart__inner--empty");

          var promo = doc.querySelector(".shipping__promo");
          if (promo) {
            inner.prepend(promo);
          }
        }

        var updatedFooter = doc.querySelector(".side-cart__footer");
        if (updatedFooter && footer) {
          footer.innerHTML = updatedFooter.innerHTML;
          footer.classList.add("side-cart__footer--visible");
        }
      })
      .then(function () {
        if (window.Douglas && typeof window.Douglas.setSideCartItemsMaxHeight === "function") {
          window.Douglas.setSideCartItemsMaxHeight();
        }
      });
  }

  function refreshCartUI(options) {
    var opts = options || {};
    var onCartPage = window.location.pathname.indexOf("/cart") > -1;

    if (onCartPage && opts.reloadCartPage) {
      window.location.reload();
      return Promise.resolve();
    }

    return refreshSideCartContents().then(function () {
      document.body.dispatchEvent(new Event("calculate__promo"));
    });
  }

  var debounceId = null;
  var running = false;
  var needsFollowUpSync = false;

  function scheduleSync() {
    if (debounceId) {
      clearTimeout(debounceId);
    }
    debounceId = setTimeout(function () {
      debounceId = null;
      runSync();
    }, SYNC_DEBOUNCE_MS);
  }

  function runSync() {
    if (getGwpConfigs().length === 0) {
      return;
    }

    if (running) {
      needsFollowUpSync = true;
      return;
    }
    running = true;
    needsFollowUpSync = false;

    var changedByRemoval = false;

    getCart()
      .then(function (cartData) {
        if (!cartData || !Array.isArray(cartData.items)) {
          return null;
        }
        return removeStaleGifts(cartData.items).then(function (removed) {
          changedByRemoval = removed;
          if (removed) {
            return getCart();
          }
          return cartData;
        });
      })
      .then(function (cartData) {
        if (cartData && Array.isArray(cartData.items)) {
          updateBanner(cartData.items);
        }
      })
      .catch(function () {
        /* leave cart untouched on failure */
      })
      .then(function () {
        running = false;
        if (changedByRemoval) {
          refreshCartUI({ reloadCartPage: true });
        }
        if (needsFollowUpSync) {
          needsFollowUpSync = false;
          scheduleSync();
        }
      });
  }

  function handleBannerAddClick(event) {
    var button = event.target.closest("[data-cart-gwp-add]");
    if (!button) {
      return;
    }

    var banner = button.closest(BANNER_SELECTOR);
    if (!banner) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var variantId = banner.getAttribute("data-gwp-variant-id");
    var configKey = banner.getAttribute("data-gwp-config-id") || "";
    if (!variantId) {
      return;
    }

    if (button.disabled || button.getAttribute("aria-busy") === "true") {
      return;
    }

    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    addGift(variantId, configKey)
      .then(function (res) {
        if (isErrorResponse(res)) {
          button.disabled = false;
          button.removeAttribute("aria-busy");
          return;
        }

        hideBanner(banner);

        return getCart()
          .then(function (cartData) {
            if (cartData && Array.isArray(cartData.items)) {
              updateBanner(cartData.items);
            }
            return refreshCartUI();
          })
          .then(function () {
            document.body.dispatchEvent(new Event("cart__updated"));
          });
      })
      .catch(function () {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      })
      .then(function () {
        if (button.isConnected) {
          button.disabled = false;
          button.removeAttribute("aria-busy");
        }
      });
  }

  function bindCartListeners() {
    if (document.body.getAttribute("data-gwp-bound") === "true") {
      return;
    }
    document.body.setAttribute("data-gwp-bound", "true");

    document.body.addEventListener("cart__updated", scheduleSync);
    document.body.addEventListener("calculate__promo", scheduleSync);
    document.body.addEventListener("click", handleBannerAddClick, true);

    document.querySelectorAll("cart-items").forEach(function (el) {
      el.addEventListener("change", scheduleSync);
    });

    document.addEventListener("shopify:section:load", scheduleSync);
  }

  function init() {
    bindCartListeners();

    if (getGwpConfigs().length > 0) {
      runSync();
    }
  }

  if (document.readyState !== "loading") {
    init();
  } else {
    document.addEventListener("DOMContentLoaded", init);
  }
})();
